import React, { useMemo } from "react";
import moment from "moment";
import { Tooltip } from "antd";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ReferenceLine,
} from "recharts";
import { useManpowerForecast } from "hooks/analytics/analytics";
import { fmt } from "components/analytics";
import "./forecast.css";

/**
 * Headcount forecast for the coming fortnight.
 *
 * Two things are deliberate here. The forecast is drawn dashed inside a shaded
 * interval so it can never be mistaken for measured attendance, and the model's
 * own backtest error is printed on the card -- if the model is doing badly, the
 * person reading it finds out from the card rather than from a bad decision.
 */

const COLOR = { observed: "#1e5dfe", forecast: "#6d47d9", band: "#6d47d9" };

const ForecastTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const isForecast = point.expected !== null && point.expected !== undefined;
  return (
    <div className="an-tip">
      <div className="an-tip-title">
        {moment(label).format("ddd, DD MMM")} {isForecast ? "· forecast" : ""}
      </div>
      {isForecast ? (
        <>
          <div className="an-tip-row"><span>Expected</span><span>{fmt(point.expected)}</span></div>
          <div className="an-tip-row">
            <span>80% range</span>
            <span>{fmt(point.lower)}–{fmt(point.upper)}</span>
          </div>
          {point.restDay && <div className="an-tip-row"><span>Rest day</span><span>yes</span></div>}
        </>
      ) : (
        <div className="an-tip-row"><span>On site</span><span>{fmt(point.observed)}</span></div>
      )}
    </div>
  );
};

const ManpowerForecast = ({ horizonDays = 14 }) => {
  const { data: res, isLoading } = useManpowerForecast(horizonDays);
  const forecast = res?.data;

  /** Recharts needs the band as a base offset plus a height, not two bounds. */
  const chartData = useMemo(() => {
    if (!forecast?.points) return [];
    return forecast.points.map((p) => ({
      date: p.date,
      expected: p.expected,
      lower: p.lower,
      bandHeight: Math.max(0, (p.upper ?? 0) - (p.lower ?? 0)),
      upper: p.upper,
      restDay: p.restDay,
    }));
  }, [forecast]);

  if (isLoading) {
    return (
      <section className="fc-card">
        <header className="fc-head"><h3>Manpower forecast</h3></header>
        <div className="fc-empty">Loading forecast…</div>
      </section>
    );
  }

  if (!forecast?.available) {
    return (
      <section className="fc-card">
        <header className="fc-head">
          <h3>Manpower forecast</h3>
        </header>
        <div className="fc-empty">
          {forecast?.message || "Forecast unavailable."}
        </div>
      </section>
    );
  }

  const nextWorking = forecast.points.find((p) => !p.restDay) || forecast.points[0];
  const accuracyTone =
    forecast.backtestMape === null || forecast.backtestMape === undefined
      ? ""
      : forecast.backtestMape <= 15
      ? "is-good"
      : forecast.backtestMape <= 25
      ? "is-fair"
      : "is-poor";

  return (
    <section className="fc-card">
      <header className="fc-head">
        <div>
          <h3>
            Manpower forecast
            <Tooltip title={forecast.method}>
              <span className="fc-info">i</span>
            </Tooltip>
          </h3>
          <p>
            Next {forecast.horizonDays} days, predicted from {forecast.historyDays} days of
            attendance history. Dashed line and shaded band are a prediction, not a record.
          </p>
        </div>
        <div className="fc-badges">
          <span className={`fc-badge ${accuracyTone}`}>
            {forecast.backtestMape !== null && forecast.backtestMape !== undefined
              ? `±${forecast.backtestMape}% typical error`
              : "accuracy pending"}
          </span>
          <span className="fc-asof">as of {moment(forecast.asOf).format("DD MMM")}</span>
        </div>
      </header>

      <div className="fc-figures">
        <div className="fc-figure">
          <span className="fc-figure-label">Next working day</span>
          <span className="fc-figure-value">{fmt(nextWorking?.expected)}</span>
          <span className="fc-figure-foot">
            {moment(nextWorking?.date).format("ddd DD MMM")} · {fmt(nextWorking?.lower)}–
            {fmt(nextWorking?.upper)}
          </span>
        </div>
        <div className="fc-figure">
          <span className="fc-figure-label">Busiest day ahead</span>
          {(() => {
            const peak = forecast.points.reduce(
              (best, p) => ((p.expected ?? 0) > (best?.expected ?? 0) ? p : best),
              forecast.points[0]
            );
            return (
              <>
                <span className="fc-figure-value">{fmt(peak?.expected)}</span>
                <span className="fc-figure-foot">{moment(peak?.date).format("ddd DD MMM")}</span>
              </>
            );
          })()}
        </div>
        <div className="fc-figure">
          <span className="fc-figure-label">Confidence range</span>
          <span className="fc-figure-value">±{fmt(forecast.intervalWidth)}</span>
          <span className="fc-figure-foot">people, 80% interval</span>
        </div>
      </div>

      <div className="fc-chart">
        <ResponsiveContainer width="100%" height={230}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: -12 }}>
            <CartesianGrid stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => moment(d).format("DD MMM")}
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              stroke="#eaecf0"
              minTickGap={18}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#94a3b8" }}
              stroke="#eaecf0"
              allowDecimals={false}
            />
            <RTooltip content={<ForecastTooltip />} />
            {/* Band drawn as an invisible base plus its height, stacked. */}
            <Area
              type="monotone"
              dataKey="lower"
              stackId="band"
              stroke="none"
              fill="transparent"
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="bandHeight"
              stackId="band"
              stroke="none"
              fill={COLOR.band}
              fillOpacity={0.14}
              isAnimationActive={false}
              name="80% interval"
            />
            <Line
              type="monotone"
              dataKey="expected"
              name="Expected headcount"
              stroke={COLOR.forecast}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={{ r: 2.5, fill: "#ffffff", stroke: COLOR.forecast, strokeWidth: 2 }}
            />
            {chartData.filter((d) => d.restDay).map((d) => (
              <ReferenceLine key={d.date} x={d.date} stroke="#eaecf0" strokeDasharray="2 3" />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <footer className="fc-foot">
        <span>
          Weekly shape learned from the last 4 weeks — Sunday runs at{" "}
          {forecast.seasonalIndex?.SUNDAY
            ? `${Math.round(forecast.seasonalIndex.SUNDAY * 100)}%`
            : "a fraction"}{" "}
          of the weekly average. Vertical guides mark rest days.
        </span>
      </footer>
    </section>
  );
};

export default ManpowerForecast;
